/**
 * Timezone-aware formatters that render visit clock times in the CLIENT's
 * local timezone (not the viewer's browser timezone).
 *
 * `tz` is an IANA timezone string like "America/New_York". Falls back to
 * "America/New_York" when nullish/empty.
 *
 * Source times are ISO strings stored in UTC by the backend.
 */

const DEFAULT_TZ = 'America/New_York';

const _safeTz = (tz) => (typeof tz === 'string' && tz.length > 0 ? tz : DEFAULT_TZ);

const _date = (iso) => {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

// "Apr 28, 9:05 AM" in client TZ
export const fmtClientTime = (iso, tz) => {
  const d = _date(iso);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: _safeTz(tz),
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d);
  } catch { return d.toLocaleString(); }
};

// "9:05 AM" in client TZ
export const fmtClientTimeOnly = (iso, tz) => {
  const d = _date(iso);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: _safeTz(tz),
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d);
  } catch { return d.toLocaleTimeString(); }
};

// "EDT" / "EST" abbreviation for the supplied TZ at a specific instant
export const tzAbbrev = (iso, tz) => {
  const d = _date(iso) || new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: _safeTz(tz), timeZoneName: 'short',
    }).formatToParts(d);
    const z = parts.find(p => p.type === 'timeZoneName');
    return z ? z.value : '';
  } catch { return ''; }
};

// "Apr 28, 9:05 AM EDT"
export const fmtClientTimeFull = (iso, tz) => {
  const t = fmtClientTime(iso, tz);
  const z = tzAbbrev(iso, tz);
  return z ? `${t} ${z}` : t;
};

// Convert browser-local datetime-local string ("2026-04-28T09:00") into a UTC ISO
// string AS IF the input had been entered in `tz` (client tz).
// This is the inverse of fmtClientTime — used when supervisor types a clock time
// in the client's tz and we need to store it in UTC.
export const clientTzInputToUtcIso = (localStr, tz) => {
  if (!localStr) return '';
  const zone = _safeTz(tz);
  // Parse the input as if it were in `zone`. We compute the offset of `zone`
  // at the nearby date and subtract it from a naive UTC reading.
  // Step 1: Parse as naive UTC.
  const naive = new Date(`${localStr}:00.000Z`);
  if (isNaN(naive.getTime())) return '';
  // Step 2: Get what `zone`'s wall-clock would be for that UTC moment.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(naive).reduce((acc, p) => { if (p.type !== 'literal') acc[p.type] = p.value; return acc; }, {});
  const wall = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour === '24' ? '00' : parts.hour, 10),
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10),
  );
  // Step 3: offset is how much `zone` is ahead/behind the naive UTC reading.
  const offsetMs = wall - naive.getTime();
  // Step 4: subtract offset to get the true UTC instant.
  return new Date(naive.getTime() - offsetMs).toISOString();
};

// Format an ISO time as "datetime-local" input value, but using the client's
// timezone instead of the browser's. Inverse of clientTzInputToUtcIso.
export const utcIsoToClientTzInput = (iso, tz) => {
  const d = _date(iso);
  if (!d) return '';
  const zone = _safeTz(tz);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d).reduce((acc, p) => { if (p.type !== 'literal') acc[p.type] = p.value; return acc; }, {});
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
};

// Common IANA timezones for a US home-care agency (used in the dropdown).
export const US_TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Phoenix',     label: 'Mountain — Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage',   label: 'Alaska' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii' },
];
